import { Box, Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { truckFieldConfiguration } from "../../../formConfigs/Fleet/truckFieldConfig";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import useAuth from "../../../hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { trailerFieldConfiguration } from "../../../formConfigs/Fleet/trailerFieldConfig";
import { capitalizeFirstChar } from "../../../helpers/formatingFunctions";
import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import {
  companyFieldConfiguration,
  companyInitialValues,
} from "../../../formConfigs/Fleet/companyFieldConfig";
import { fetchLoans } from "../LoanTable/LoanTable";
import LoanPopup from "../LoanPopup/LoanPopup";

const UpdateAssetPopup = ({
  asset,
  assetType,
  id,
}: {
  asset: any;
  assetType: string;
  id: string | undefined;
}) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openCompany, setOpenCompany] = useState(false);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: ownerCompaniesData } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!token,
  });

  const { data: loansDataRaw } = useQuery({
    queryKey: ["loans"],
    queryFn: () =>
      fetchLoans(token, new Date().toISOString().slice(0, 16), true).then(
        (response) =>
          response.map((loan: any) => ({
            label: `${loan.loanNumber} - ${loan.lenderName} - $${loan.monthlyPayment}`,
            id: loan.id,
          }))
      ),
    enabled: !!open,
  });

  const ownerCompanies = useMemo(
    () => ownerCompaniesData ?? [],
    [ownerCompaniesData]
  );

  const loansData = useMemo(() => loansDataRaw ?? [], [loansDataRaw]);

  const [truckInitialValues, setTruckInitialValues] = useState<any>({});
  const [trailerInitialValues, setTrailerInitialValues] = useState<any>({});

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/update${assetType}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      assetType === "truck"
        ? queryClient.invalidateQueries({ queryKey: ["trucksTable"] })
        : queryClient.invalidateQueries({ queryKey: ["trailersTable"] });

      queryClient.invalidateQueries({ queryKey: ["asset", id, assetType] });
      queryClient.invalidateQueries({ queryKey: ["companyHistory"] });
      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit truck");
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      companyOwnerId: getId(data.companyOwnerId),
      loanId: getId(data.loanId),
    };

    mutation.mutate(finalData);
  };

  useEffect(() => {
    const updatedTruckValues = {
      id: id,
      unitNumber: asset.unitNumber,
      make: asset.make,
      model: asset.model,
      year: asset.year,
      VIN: asset.vin,
      licensePlate: asset.licensePlate,
      state: asset.state.toUpperCase(),
      tireSize: asset.tireSize,
      status: asset.status,
      engineType: capitalizeFirstChar(asset.engineType),
      companyOwnerId: ownerCompanies.find(
        (company: any) => company.id === asset.companyOwnerId
      ),
      loanId: loansData.find((loan: any) => loan.id === asset.loanId),
    };

    const updatedTrailerValues = {
      id: id,
      unitNumber: asset.unitNumber,
      make: asset.make,
      model: asset.model,
      year: asset.year,
      VIN: asset.vin,
      licensePlate: asset.licensePlate,
      state: asset.state.toUpperCase(),
      tireSize: asset.tireSize,
      status: asset.status,
      companyOwnerId: ownerCompanies.find(
        (company: any) => company.id === asset.companyOwnerId
      ),
      loanId: loansData.find((loan: any) => loan.id === asset.loanId),
      trailerType:
        asset.trailerType === "rgn"
          ? asset.trailerType.toUpperCase()
          : capitalizeFirstChar(asset.trailerType),
      length: asset.length,
    };

    setTruckInitialValues(updatedTruckValues);
    setTrailerInitialValues(updatedTrailerValues);
  }, [ownerCompanies, asset, id, loansData]);

  const companyMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/ownerCompany`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["ownerCompanies"] });
      setOpenCompany(false);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit company");
    },
  });

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update Asset
      </Button>

      {open &&
        (assetType === "truck"
          ? truckInitialValues.id
          : trailerInitialValues.id) && (
          <GenericFormPopup
            formFields={
              assetType === "truck"
                ? truckFieldConfiguration
                : trailerFieldConfiguration
            }
            initialValues={
              assetType === "truck" ? truckInitialValues : trailerInitialValues
            }
            title="Update Asset"
            onSubmit={submitHandler}
            open={open}
            onClose={handleClose}
            loading={mutation.isPending}
            autocompleteData={{
              ownerCompanies: ownerCompanies,
              loansData: loansData,
            }}
            fieldComponents={{
              companyOwnerId: (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenCompany(true)}
                  >
                    Add Company
                  </Button>
                </Box>
              ),
              loanId: (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <LoanPopup />
                </Box>
              ),
            }}
          />
        )}
      <GenericFormPopup
        formFields={companyFieldConfiguration}
        initialValues={companyInitialValues}
        title="Company Form"
        onSubmit={(data) => {
          companyMutation.mutate(data);
        }}
        open={openCompany}
        onClose={() => setOpenCompany(false)}
        loading={companyMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage={"Asset updated successfully"}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateAssetPopup;
